import { useContext, useEffect, useMemo, useState } from 'react';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import ViewerContentStandard from './ViewerContentStandard';
import ViewerContentVirtualized from './ViewerContentVirtualized';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'foundations-theming/utils';
import { useValidateCondition } from '../../hooks/useValidateCondition';
import ViewerHeader from './ViewerHeader';
import QuickFilters from '../quickFilters/QuickFilters';
import SegmentationFiltersEditor from '../editor/SegmentationFiltersEditor';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { useEditorActions } from '../../hooks/useEditorActions';
import { hasFlexibleLogicGroupConditions } from '../../permissions/gates';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VIRTUALIZED_FILTERS_THRESHOLD = 50;
const MainContainer = styled.div.withConfig({
  displayName: "SegmentationFiltersViewer__MainContainer",
  componentId: "sc-1qw1owb-0"
})(["display:flex;flex-direction:column;height:100%;overflow:hidden;"]);
const SegmentationFiltersEditorContainer = styled.div.withConfig({
  displayName: "SegmentationFiltersViewer__SegmentationFiltersEditorContainer",
  componentId: "sc-1qw1owb-1"
})(["height:80vh;"]);
function SegmentationFiltersViewer() {
  const [isValidated, setIsValidated] = useState(false);
  const [isViewerRendered, setIsViewerRendered] = useState(false);
  const {
    isFieldSelectMode,
    isInEditMode,
    isLoadingViewerData,
    internalDraftValue,
    dispatch,
    props: {
      strategy,
      disableVirtualization,
      isAndGroup,
      onViewerRender,
      quickFilters,
      useOnePanel,
      canEditLogicGroupConditions = false
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    validateLogicGroup
  } = useValidateCondition();
  const {
    onConditionSelectCancel
  } = useEditorActions();
  useEffect(() => {
    if (!isLoadingViewerData && internalDraftValue && !isValidated) {
      dispatch({
        type: 'internalDraftValue',
        payload: validateLogicGroup(internalDraftValue)
      });
      setIsValidated(true);
    }
  }, [isLoadingViewerData, internalDraftValue, validateLogicGroup, dispatch, isValidated]);
  useEffect(() => {
    if (isViewerRendered) {
      onViewerRender();
    }
  }, [isViewerRendered, onViewerRender]);
  const showLogicGroupOperatorToggles = useMemo(() =>
  // has the consuming app opted-in
  canEditLogicGroupConditions &&
  // does the strategy support it (only ILS has supported)
  strategy.canEditLogicGroupCondition && hasFlexibleLogicGroupConditions() && isInEditMode, [canEditLogicGroupConditions, strategy.canEditLogicGroupCondition, isInEditMode]);
  if (isLoadingViewerData) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "newCustomerDataFilters.general.loadingFilters"
      }),
      showLabel: true,
      size: "small"
    });
  }
  if (!internalDraftValue || !isValidated) {
    return null;
  }
  if (!isViewerRendered) {
    setIsViewerRendered(true);
  }
  const onOpenChange = evt => {
    if (!evt.target.value) {
      onConditionSelectCancel();
    }
  };
  const shouldUseVirtualization = !disableVirtualization && !isAndGroup && internalDraftValue.conditions.size > VIRTUALIZED_FILTERS_THRESHOLD;
  const header = /*#__PURE__*/_jsxs(_Fragment, {
    children: [quickFilters && /*#__PURE__*/_jsx(QuickFilters, {}), /*#__PURE__*/_jsx(ViewerHeader, {})]
  });
  return /*#__PURE__*/_jsx(Popover, {
    animateOnToggle: false,
    open: useOnePanel && isFieldSelectMode,
    onOpenChange: onOpenChange,
    content: /*#__PURE__*/_jsx(SegmentationFiltersEditorContainer, {
      children: /*#__PURE__*/_jsx(SegmentationFiltersEditor, {})
    }),
    placement: "right",
    children: /*#__PURE__*/_jsxs(MainContainer, {
      "data-test-id": "fr-viewer",
      children: [!useOnePanel && header, shouldUseVirtualization ? /*#__PURE__*/_jsx(ViewerContentVirtualized, {
        inputUse: useOnePanel ? 'on-dark' : undefined,
        header: useOnePanel ? header : undefined,
        showLogicGroupOperatorToggles: showLogicGroupOperatorToggles
      }) : /*#__PURE__*/_jsx(ViewerContentStandard, {
        inputUse: useOnePanel ? 'on-dark' : undefined,
        header: useOnePanel ? header : undefined,
        showLogicGroupOperatorToggles: showLogicGroupOperatorToggles
      })]
    })
  });
}
export default SegmentationFiltersViewer;